/** @jsxImportSource @emotion/react */
import { useCallback, useContext, useEffect, useRef } from 'react';
import isMobile from 'ismobilejs';
import { css } from '@emotion/react';
import { AgentProgressTimeline } from '../Chat.AgentProgress/Timeline';
import { useAgentProgressTimeline } from '../Chat.AgentProgress/useTimeline';
import { SetPendingScrollContext } from './context';
import { useIsFullPageChat } from './hooks/useIsFullPageChat';
import { SystemAuthor } from './Message.Author';
import { extendedMessageWidth, fullChatExtendedMessageWidth } from './Message.styles';
import { ChainOfThoughtSteps, useChainOfThoughtSteps } from './Message.System.ChainOfThought';
import { PlaceholderMessageSkeleton } from './Message.System.Placeholder';
import { usePlaceholderStyles } from './Message.System.styles';

export const SystemUserInFlightResponse = () => {
  const device = isMobile();
  const styles = usePlaceholderStyles();

  const steps = useChainOfThoughtSteps();

  const { timeline, cardState } = useAgentProgressTimeline();

  const setPendingScroll = useContext(SetPendingScrollContext);
  const prevNodeCountRef = useRef(timeline.length);

  // Auto-scroll when new timeline events appear
  useEffect(() => {
    if (timeline.length > prevNodeCountRef.current) {
      setPendingScroll?.({ behavior: 'smooth' });
    }
    prevNodeCountRef.current = timeline.length;
  }, [timeline.length, setPendingScroll]);

  const renderBody = useCallback(() => {
    if (timeline.length > 0) {
      return (
        <div>
          <AgentProgressTimeline nodes={timeline} cardState={cardState} />
        </div>
      );
    }

    if (steps.length > 0) {
      return <ChainOfThoughtSteps steps={steps} />;
    }

    return <PlaceholderMessageSkeleton />;
  }, [steps, timeline, cardState]);

  const isFullPageChat = useIsFullPageChat();

  const bodyWidth = (() => {
    if (timeline.length === 0) return undefined;
    return isFullPageChat ? fullChatExtendedMessageWidth : extendedMessageWidth;
  })();

  return (
    <div css={styles.root}>
      {!device.phone && (
        <div css={styles.authorWrap}>
          <SystemAuthor animate={true} css={styles.author} />
        </div>
      )}
      <div css={css({ maxWidth: bodyWidth, width: '100%' })}>
        <div css={css([styles.body, css({ maxWidth: bodyWidth, width: '100%' })])}>{renderBody()}</div>
      </div>
    </div>
  );
};
